import { useNavigate, useParams } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import { useAuditoriaMutation } from "../../../services/docs";
import { useForm } from "../../../hooks/useForm";
import { Spinner } from "../../../components/Spinner";

export const AuditoriaScreen = () => {
  const { siniestro, tramite_id = "0" } = useParams();
  const navigate = useNavigate();

  // Mutation
  const [auditoria, { isLoading: isLoadingBoton }] = useAuditoriaMutation();

  // Custom Form Hook
  const { formulario, handleSelectChange, handleTexareChange } = useForm({
    fi_resolucion_auditoria: "",
    fc_comentarios_auditoria: "",
    tramite_id: Number(tramite_id),
    siniestro: siniestro || "",
  });

  const handleAction = async () => {
    // 1. Validaciones previas
    if (!formulario.fi_resolucion_auditoria) {
      toastr.error("Debe seleccionar una resolución para la auditoría");
      return;
    }
    if (formulario.fi_resolucion_auditoria === "2" && !formulario.fc_comentarios_auditoria.trim()) {
      toastr.error("Debe agregar un comentario justificando el dictamen incorrecto");
      return;
    }

    // 2. Envío a la API
    try {
      const resp = await auditoria({
        ...formulario,
        tramite_id: +tramite_id,
        fi_resolucion_auditoria: +formulario.fi_resolucion_auditoria,
      }).unwrap();

      if (resp) {
        toastr.success(resp.message || "Auditoría registrada correctamente");
        navigate(`/view/siniestro/${siniestro}/${tramite_id}/`);
      }
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      } else {
        toastr.error(err?.data?.message || "Ocurrió un error al procesar la auditoría");
      }
    }
  };

  return (
    <div className="space-y-6 pt-4">
      {/* Contenedor principal centrado para no estirar el formulario en pantallas grandes */}
      <div className="mx-auto max-w-3xl">
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm">

          {/* --- Cabecera de la Tarjeta --- */}
          <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
            <h2 className="flex items-center gap-2 text-sm font-bold text-slate-800">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
              </div>
              Resolución de Auditoría
            </h2>
            <p className="ml-9 mt-1 text-[11px] text-slate-500">
              Evalúe la integración del expediente y emita su veredicto final.
            </p>
          </div>

          {/* --- Cuerpo del Formulario --- */}
          <div className="flex flex-col gap-6 p-6">

            {/* Campo: Respuesta */}
            <div>
              <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-600">
                Dictamen / Respuesta
              </label>
              <select
                name="fi_resolucion_auditoria"
                onChange={handleSelectChange}
                value={formulario.fi_resolucion_auditoria}
                className="h-11 w-full cursor-pointer rounded-xl border border-slate-200 bg-slate-50/50 px-3 text-sm font-medium text-slate-900 transition-all hover:bg-slate-50 focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
              >
                <option value="" disabled>Selecciona el veredicto...</option>
                <option value="1">Dictamen Correcto</option>
                <option value="2">Dictamen Incorrecto</option>
              </select>
            </div>

            {/* Campo: Comentarios */}
            <div>
              <label className="mb-1.5 flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-slate-600">
                <span>Comentarios y Observaciones</span>
                {formulario.fi_resolucion_auditoria === "2" && (
                  <span className="text-[10px] font-semibold text-rose-500">* Requerido para dictámenes incorrectos</span>
                )}
              </label>
              <textarea
                name="fc_comentarios_auditoria"
                onChange={handleTexareChange}
                value={formulario.fc_comentarios_auditoria}
                placeholder="Detalle sus observaciones, hallazgos o justificación..."
                rows={4}
                className={`w-full rounded-xl border bg-slate-50/50 px-3 py-2 text-sm font-medium text-slate-900 transition-all focus:bg-white focus:outline-none focus:ring-2 ${formulario.fi_resolucion_auditoria === "2" && !formulario.fc_comentarios_auditoria.trim()
                    ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/20"
                    : "border-slate-200 focus:border-brand-500 focus:ring-brand-500/20"
                  }`}
              ></textarea>
            </div>

          </div>

          {/* --- Footer de Acción --- */}
          <div className="flex items-center justify-end border-t border-slate-100 bg-slate-50/50 p-4">
            <button
              onClick={handleAction}
              disabled={isLoadingBoton}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-6 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-brand-700 disabled:opacity-50"
            >
              {isLoadingBoton ? <Spinner /> : (
                <>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                  Enviar Auditoría
                </>
              )}
            </button>
          </div>

        </section>
      </div>
    </div>
  );
};
